import React, { useMemo, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';

const SEV_COLOR = { critical: 'error', error: 'error', warning: 'warning', info: 'info' };

/**
 * Groups rule results by rule and lets the operator resolve (data fixed) or
 * override (accept with reason) each one. Resolved/overridden results are
 * preserved across recalc by the server.
 *
 * Props: ruleResults[], onResolve(id), onOverride(id, reason),
 *        onJumpToRow(rowId)? (optional).
 */
function ExceptionQueue({ ruleResults = [], onResolve, onOverride, onJumpToRow, busy }) {
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [reason, setReason] = useState('');

  const groups = useMemo(() => {
    const m = new Map();
    for (const r of ruleResults) {
      const rule = r.payment_rules || {};
      const key = rule.code || r.rule_id || 'unknown';
      if (!m.has(key)) {
        m.set(key, { code: key, name: rule.name || key, category: rule.category, severity: r.severity, items: [] });
      }
      m.get(key).items.push(r);
    }
    return [...m.values()].sort((a, b) => b.items.length - a.items.length);
  }, [ruleResults]);

  const openOverride = (r) => { setOverrideTarget(r); setReason(''); };
  const confirmOverride = () => {
    if (overrideTarget && reason.trim()) {
      onOverride(overrideTarget.id, reason.trim());
      setOverrideTarget(null);
      setReason('');
    }
  };

  if (ruleResults.length === 0) {
    return <Alert severity="success">No open exceptions on this batch.</Alert>;
  }

  return (
    <Box>
      {groups.map((g) => {
        const open = g.items.filter((i) => i.status === 'open').length;
        return (
          <Accordion key={g.code} defaultExpanded={open > 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={g.severity} color={SEV_COLOR[g.severity] || 'default'} size="small" />
                <Typography fontWeight="medium">{g.name}</Typography>
                <Chip label={`${g.items.length} total`} size="small" variant="outlined" />
                {open > 0 && <Chip label={`${open} open`} color="warning" size="small" />}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Message</TableCell>
                    <TableCell>Field</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {g.items.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ maxWidth: 360 }}>{r.message}</TableCell>
                      <TableCell>{r.field_name || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.status}
                          size="small"
                          color={r.status === 'open' ? 'default' : r.status === 'overridden' ? 'warning' : 'success'}
                          variant={r.status === 'open' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {r.status === 'open' ? (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            {onJumpToRow && r.payment_row_id && (
                              <Button size="small" onClick={() => onJumpToRow(r.payment_row_id)}>Row</Button>
                            )}
                            <Button size="small" disabled={busy} onClick={() => onResolve(r.id)}>Resolve</Button>
                            <Button size="small" color="warning" disabled={busy} onClick={() => openOverride(r)}>Override</Button>
                          </Box>
                        ) : (
                          r.override_reason ? <Typography variant="caption" color="text.secondary">{r.override_reason}</Typography> : null
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Dialog open={!!overrideTarget} onClose={() => setOverrideTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Override exception</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>{overrideTarget?.message}</Typography>
          <TextField
            label="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={2}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideTarget(null)}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={!reason.trim()} onClick={confirmOverride}>
            Override
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ExceptionQueue;
